import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { authRedirectUrl } from '@/lib/constants';

export default function ResetPasswordPage() {
  const [email, setEmail] = useState('info@leomelcherts.com');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: "Passwords don't match",
        description: 'Please make sure your passwords match.',
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        variant: 'destructive',
        title: 'Password too short',
        description: 'Password must be at least 8 characters long.',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Get the current user's session token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        toast({
          variant: 'destructive',
          title: 'Not authenticated',
          description:
            'You must be logged in as an admin to perform this action.',
        });
        setIsLoading(false);
        return;
      }

      // For direct password reset without edge function
      try {
        // Generate a random password reset token
        const { data: resetData, error: resetError } =
          await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: authRedirectUrl('/admin/reset-password-confirm'),
          });

        if (resetError) throw resetError;

        // Show success message
        toast({
          title: 'Password reset email sent',
          description: 'A password reset email has been sent to ' + email,
        });

        return;
      } catch (resetErr) {
        console.error('Error sending reset email:', resetErr);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to send password reset email. Please try again.',
        });
      }

      // This block was referencing an undefined error variable
      // Removed as it's after a try/catch that would have caught any errors

      toast({
        title: 'Success',
        description: 'Password has been reset successfully.',
      });

      // Clear the form
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          error.message || 'Failed to reset password. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-10 max-w-md">
      <Card className="w-full bg-white">
        <CardHeader>
          <CardTitle>Reset User Password</CardTitle>
          <CardDescription>Reset the password for {email}</CardDescription>
        </CardHeader>
        <form onSubmit={handleResetPassword}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
